import * as React from "react";
import { isEmpty } from "lodash";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import {
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  InputGroup,
  InputGroupAddon,
  FormFeedback,
  Input
} from "reactstrap";
import { getParingDataError, pairData } from "../../api/devices";
import PageLayout from "../../components/PageLayout";
import { useAuth0 } from "../../auth/react-auth0-wrapper";
import { DeviceLabelList } from "../../components/Barcode";
import { printLabels } from "../../utils/content";

const DeviceSimPairing = () => {
  const totalInput = React.createRef();
  const [loading, setLoading] = React.useState();
  const [focused, setFocused] = React.useState("-");
  const [scans, setScans] = React.useState([]);
  const [errors, setErrors] = React.useState({});
  const [paired, setPaired] = React.useState([]);
  const [shouldClose, setShouldClose] = React.useState(false);
  const { onSuccess, onError } = useAuth0();

  const getEmptySlot = () => ({
    device: "",
    sim: ""
  });

  React.useEffect(() => {
    //console.log(focused);
    if (focused !== "-") {
      document.getElementById(focused).focus();
    }
  }, [focused, scans.length]);

  const getSimId = i => (i > -1 ? `simcard-${i}` : "-");
  const getDeviceId = i => (i > -1 ? `device-${i}` : "-");

  const validate = async scanlist => {
    try {
      setLoading(true);
      let valErrors = scanlist.reduce((err, el, i) => {
        const simId = getSimId(i);
        const devId = getDeviceId(i);
        if (
          el.device.length !== 15 ||
          scanlist.filter(dev => dev.device === el.device).length > 1
        ) {
          err[
            devId
          ] = `Inserire un IMEI valido (lunghezza 15) e non inserito in precedenza`;
        }
        if (
          !el.sim.startsWith("8939") ||
          scanlist.filter(sm => sm.sim === el.sim).length > 1
        ) {
          err[
            simId
          ] = `Inserire un codice iccid valido (deve iniziare con 8939) e non inserito in precedenza`;
        }
        return err;
      }, {});

      if (!isEmpty(valErrors)) {
        setErrors(valErrors);
      } else {
        valErrors = await getParingDataError(
          scanlist,
          getDeviceId,
          getSimId
        );
        setErrors(valErrors);
      }
      return isEmpty(valErrors);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFocused("-");
    setErrors({});
    setShouldClose(false);
    setScans([]);
    setPaired([]);
  };

  const onSetTotalDevices = () => {
    const numDev = Number(totalInput.current.element.value);
    const slots = new Array(numDev);
    slots.fill(getEmptySlot());
    setScans(slots);
    moveNext(slots);
  };

  const closeModal = () => {
    setShouldClose(!shouldClose);
  };

  const removePair = async index => {
    const idDev = getDeviceId(index);
    const idSim = getSimId(index);
    const newList = [...scans.slice(0, index), ...scans.slice(index + 1)];

    setScans(newList);

    if (newList.length === 0) {
      setFocused("-");
      setShouldClose(false);
    }
    await validate(newList);
    moveNext(newList);
  };

  const addPair = async () => {
    const newList = scans.concat(getEmptySlot());
    setScans(newList);
    await validate(newList);
    moveNext(newList);
  };

  const moveNext = list => {
    let nextIndex = -1;
    let nextType = "";
    const firstEmptyDeviceSlot = list.findIndex(s => !s.device);
    const firstEmptySimSlot = list.findIndex(s => !s.sim);
    if (firstEmptyDeviceSlot > -1) {
      nextIndex = firstEmptyDeviceSlot;
      nextType = "device";
    } else if (firstEmptySimSlot > -1) {
      nextIndex = firstEmptySimSlot;
      nextType = "sim";
    }
    const id =
      nextType === "device" ? getDeviceId(nextIndex) : getSimId(nextIndex);
    setFocused(id);
  };

  const onChange = async (value, type, index) => {
    try {
      const newScans = scans.map((el, i) => {
        return i === index
          ? {
            device: type === "device" ? value : el.device,
            sim: type === "sim" ? value : el.sim
          }
          : el;
      });
      setScans(newScans);
      await validate(newScans);
    } catch (error) {
      onError();
    }
  };

  const onCheckReturn = e => {
    if (e.key === "Enter") {
      moveNext(scans);
    }
  };

  const saveData = async () => {
    try {
      if (!scans.length) return;
      setLoading(true);
      const isValid = await validate(scans);
      if (!isValid) return;
      const pairedSummary = await pairData(scans);
      setPaired(pairedSummary);
      onSuccess();
    } catch (error) {
      onError();
    } finally {
      setLoading(false);
    }
  };

  const printLabelsSection = () => (
    <Modal centered isOpen={paired && paired.length > 0} backdrop={true}>
      <ModalHeader>Stampa etichette</ModalHeader>
      <ModalBody>
        <DeviceLabelList id="labelsToPrint" items={paired} />
      </ModalBody>
      <ModalFooter>
        <div className="float-right mt-5">
          <Button
            className="p-button-success mr-2 mb-2 mr-2 mb-2"
            onClick={() => printLabels("labelsToPrint")}
            label="Stampa"
            icon="pi pi-check"
            iconPos="right"
          />

          <Button
            className="p-button-warning"
            label="Chiudi"
            icon="pi pi-times"
            onClick={resetForm}
          />
        </div>
      </ModalFooter>
    </Modal>
  );

  const totalNumberEditor = () => (
    <Modal
      isOpen={scans.length === 0 && !shouldClose}
      centered
      backdrop={true}
      onOpened={() => {
        totalInput.current.element.focus();
      }}
    >
      <ModalHeader toggle={() => setShouldClose(!shouldClose)}>
        Inserire il numero di dispositivi da associare
      </ModalHeader>
      <ModalBody>
        <InputText
          ref={totalInput}
          keyfilter="int"
          onKeyPress={e => {
            if (e.key === "Enter") {
              onSetTotalDevices();
            }
          }}
          placeholder="totale dispositivi"
        />
      </ModalBody>
      <ModalFooter>
        <Button
          className="p-button-warning mr-2"
          onClick={closeModal}
          label="Annulla"
          icon="pi pi-times"
          iconPos="right"
        />
        <Button
          onClick={onSetTotalDevices}
          label="Inizia"
          icon="pi pi-check"
          iconPos="right"
        />
      </ModalFooter>
    </Modal>
  );

  const getScanner = () => {
    return scans.map((el, i) => {
      const idSim = getSimId(i);
      const idDevice = getDeviceId(i);
      const validDevice = errors[idDevice] ? false : true;
      const validSim = errors[idSim] ? false : true;
      return (
        <Row key={i} className="mt-2">
          <Col sm="5" className="mt-2">
            <InputGroup>
              <InputGroupAddon addonType="prepend">IMEI</InputGroupAddon>
              <Input
                id={idDevice}
                tabIndex={i + 1}
                placeholder="IMEI"
                maxLength={15}
                value={el.device}
                onKeyPress={onCheckReturn}
                onFocus={() => setFocused(idDevice)}
                onChange={e => onChange(e.target.value, "device", i)}
                invalid={!validDevice}
              />
              {!validDevice && (
                <FormFeedback invalid="invalid">
                  {errors[idDevice]}
                </FormFeedback>
              )}
            </InputGroup>
          </Col>
          <Col sm="5" className="mt-2">
            <InputGroup>
              <InputGroupAddon addonType="prepend">ICCID</InputGroupAddon>
              <Input
                placeholder="ICCID"
                id={idSim}
                tabIndex={i + 1 + scans.length}
                invalid={!validSim}
                value={el.sim}
                onFocus={() => setFocused(idSim)}
                onKeyPress={onCheckReturn}
                onChange={e => onChange(e.target.value, "sim", i)}
              />
              {!validSim && (
                <FormFeedback invalid="invalid">{errors[idSim]}</FormFeedback>
              )}
            </InputGroup>
          </Col>
          <Col sm="2" className="mt-2">
            <Button
              tabIndex={-1}
              icon="pi pi-times"
              onClick={() => removePair(i)}
            />
          </Col>
        </Row>
      );
    });
  };

  return (
    <PageLayout
      title="Associazione DISPOSITIVO - SIM"
      optionalNav={{ link: "/backoffice/devices", text: "Dispositivi" }}
    >
      {totalNumberEditor()}
      <Row>
        <Col>
          <Button label="Aggiungi coppia" icon="pi pi-plus" onClick={addPair} />
        </Col>
      </Row>
      {getScanner()}
      {printLabelsSection()}
      <Row>
        <Col>
          <div className="float-right mt-5">
            <Button
              disabled={loading || !isEmpty(errors)}
              className="p-button-success mr-2 mb-2"
              label="Salva"
              type="button"
              icon={loading ? "pi pi-spin pi-spinner" : "pi pi-check"}
              onClick={saveData}
            />

            <Button
              className="p-button-warning"
              disabled={loading}
              label="Reset"
              icon="pi pi-times"
              onClick={resetForm}
            />
          </div>
        </Col>
      </Row>
    </PageLayout>
  );
};

export default DeviceSimPairing;
